import { Status } from "@googlemaps/react-wrapper"
import { createCustomEqual } from "fast-equals"
import React, { PropsWithChildren } from "react"

const isLatLngLiteral = (obj: unknown): obj is google.maps.LatLngLiteral =>
  obj != null &&
  typeof obj === "object" &&
  Number.isFinite((obj as google.maps.LatLngLiteral).lat) &&
  Number.isFinite((obj as google.maps.LatLngLiteral).lng)

interface MapProps extends PropsWithChildren<google.maps.MapOptions> {
  onClick?: (e: google.maps.MapMouseEvent) => void
  onIdle?: (map: google.maps.Map) => void
  style: { [key: string]: string }
}

const deepCompareEqualsForMaps = createCustomEqual((deepEqual) => (a: unknown, b: unknown) => {
  if (
    (isLatLngLiteral(a) || a instanceof google.maps.LatLng) &&
    (isLatLngLiteral(b) || b instanceof google.maps.LatLng)
  ) {
    return new google.maps.LatLng(a).equals(new google.maps.LatLng(b))
  }

  // TODO extend to other types

  // use fast-equals for other objects
  return deepEqual(a, b)
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useDeepCompareMemoize(value: any) {
  const ref = React.useRef()

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value
  }

  return ref.current
}

function useDeepCompareEffectForMaps(callback: React.EffectCallback, dependencies: unknown[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize))
}

export const GoogleMap: React.FC<MapProps> = ({ children, onClick, onIdle, style, ...options }) => {
  const ref = React.useRef<HTMLDivElement>(null)

  const [map, setMap] = React.useState<google.maps.Map>()

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {}))
    }
  }, [ref, map])

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options)
    }
  }, [map, options])

  React.useEffect(() => {
    if (map) {
      // eslint-disable-next-line @typescript-eslint/no-extra-semi
      ;["click", "idle"].forEach((eventName) => google.maps.event.clearListeners(map, eventName))

      if (onClick) {
        map.addListener("click", onClick)
      }

      if (onIdle) {
        map.addListener("idle", () => onIdle(map))
      }
    }
  }, [map, onClick, onIdle])

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, { map })
        }
      })}
    </>
  )
}

export const GoogleMapMarker: React.FC<google.maps.MarkerOptions> = (options) => {
  const [marker, setMarker] = React.useState<google.maps.Marker>()

  React.useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.Marker())
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null)
      }
    }
  }, [marker])

  React.useEffect(() => {
    if (marker) {
      marker.setOptions(options)
    }
  }, [marker, options])

  return null
}

export const renderMap = (status: Status) => {
  return <h1>{status}</h1>
}
